import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Paper,
  Fade,
  IconButton,
  Alert,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineRounded';
import CloseIcon from '@mui/icons-material/CloseRounded';

export interface ReportProgressState {
  active: boolean;
  step: string;
  current: number;
  total: number;
  result?: {
    success: boolean;
    sent?: number;
    skipped?: number;
    total?: number;
    error?: string;
  };
}

interface ReportProgressOverlayProps {
  progress: ReportProgressState;
  onDismiss: () => void;
}

const ReportProgressOverlay: React.FC<ReportProgressOverlayProps> = ({ progress, onDismiss }) => {
  if (!progress.active) return null;

  const percentage = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;
  const isComplete = progress.result !== undefined;
  const isError = isComplete && !progress.result?.success;
  const isSuccess = isComplete && progress.result?.success;

  return (
    <Fade in={progress.active}>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1400,
          width: 400,
          maxWidth: 'calc(100vw - 48px)',
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: '#1A1C1F',
          border: '1px solid rgba(172,179,180,0.15)',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
            background: isError
              ? 'linear-gradient(135deg, #D41C4A 0%, #1A1C1F 100%)'
              : isSuccess
              ? 'linear-gradient(135deg, #0AFFC6 0%, #1A1C1F 100%)'
              : 'linear-gradient(135deg, #F1B1FF 0%, #1A1C1F 100%)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {isSuccess && <CheckCircleOutlineIcon sx={{ color: '#0AFFC6', fontSize: 22 }} />}
            {isError && <ErrorOutlineIcon sx={{ color: '#FF4466', fontSize: 22 }} />}
            <Typography variant="subtitle2" fontWeight={600} color="#EDF7FF">
              {isSuccess ? 'Report Sent' : isError ? 'Report Failed' : 'Generating Report...'}
            </Typography>
          </Box>
          {isComplete && (
            <IconButton size="small" onClick={onDismiss} sx={{ color: '#ACB3B4' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ px: 2.5, py: 2 }}>
          {/* Progress bar (visible while processing) */}
          {!isComplete && (
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Progress
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {percentage}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={percentage}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'rgba(172,179,180,0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    background: 'linear-gradient(90deg, #F1B1FF 0%, #0AFFC6 100%)',
                  },
                }}
              />
            </Box>
          )}

          {/* Step description */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: '0.82rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {progress.step}
          </Typography>

          {/* Result summary */}
          {isSuccess && progress.result && (
            <Alert
              severity="success"
              icon={false}
              sx={{
                mt: 1.5,
                bgcolor: 'rgba(10,255,198,0.08)',
                border: '1px solid rgba(10,255,198,0.2)',
                borderRadius: 2,
                '& .MuiAlert-message': { color: '#EDF7FF', width: '100%' },
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                {progress.result.sent} report{progress.result.sent !== 1 ? 's' : ''} sent successfully
                {(progress.result.skipped ?? 0) > 0 && ` (${progress.result.skipped} skipped)`}
              </Typography>
            </Alert>
          )}

          {isError && progress.result?.error && (
            <Alert
              severity="error"
              icon={false}
              sx={{
                mt: 1.5,
                bgcolor: 'rgba(212,28,74,0.08)',
                border: '1px solid rgba(212,28,74,0.2)',
                borderRadius: 2,
                '& .MuiAlert-message': { color: '#EDF7FF' },
              }}
            >
              <Typography variant="body2">
                {progress.result.error}
              </Typography>
            </Alert>
          )}
        </Box>
      </Paper>
    </Fade>
  );
};

export default ReportProgressOverlay;
